import { useT } from "../i18n/LanguageProvider";
import { C } from "../styles/tokens";

/**
 * StatusBadge - the standard uppercase, dot-prefixed status pill.
 * Used for credentials, requests, and org statuses across the app.
 *
 * Pass a `status` enum value; the component picks the colour and translates
 * the label via i18n.
 *
 * Sizes: "sm" (default) for inline use, "lg" for dashboard cards.
 */
export function StatusBadge({ status, size = "sm" }) {
  const t = useT();
  const config = configFor(status, t);

  const dotSize = size === "lg" ? 7 : 5;
  const fontSize = size === "lg" ? 11 : 10;
  const padding = size === "lg" ? "3px 10px" : "1px 8px";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding,
        fontSize,
        fontWeight: 700,
        color: config.color,
        background: config.bg,
        borderRadius: 10,
        letterSpacing: 0.4,
        textTransform: "uppercase",
      }}
    >
      <span
        style={{
          width: dotSize,
          height: dotSize,
          borderRadius: "50%",
          background: config.color,
        }}
      />
      {config.label}
    </span>
  );
}

function configFor(status, t) {
  switch (status) {
    case "NONE":
      return { bg: C.divider, color: C.textMed, label: t("status.unverified") };
    case "PENDING":
      return { bg: C.amberLight, color: C.amber, label: t("status.pending") };
    case "VERIFIED":
      return { bg: C.greenLight, color: C.green, label: t("status.verified") };
    case "REJECTED":
      return { bg: C.redLight, color: C.red, label: t("status.rejected") };
    case "WITHDRAWN":
      return { bg: C.divider, color: C.textMed, label: t("status.withdrawn") };
    case "APPROVED":
      return { bg: C.greenLight, color: C.green, label: t("status.approved") };
    case "SUSPENDED":
      return { bg: C.redLight, color: C.red, label: t("status.suspended") };
    case "PENDING_REVIEW":
      return {
        bg: C.amberLight,
        color: C.amber,
        label: t("status.pendingReview"),
      };
    default:
      return { bg: C.divider, color: C.textMed, label: status };
  }
}
